'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import UILink from 'UIComponents/link/UILink';
import { wrapReduxAction } from 'AdsUI/lib/ActionWrapper';
import Actions from 'AdsUI/lib/Actions';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import * as AudiencesData from 'AdsUI/data/AudiencesData';
import ContactsData from 'AdsUI/data/ContactsData';
import { addHeaderBanner, removeHeaderBanner } from 'AdsUI/actions/BannerActions';
import BannerTypes from 'AdsUI/lib/BannerTypes';
import { Networks, AudienceTypes, VisitorRuleTypes } from 'AdsUI/lib/Constants';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { getRemainingAudienceCount } from 'AdsUI/selectors/AudienceCreationSelectors';
import { Metrics } from 'AdsUI/Metrics';
const audienceParamsMap = {
  [VisitorRuleTypes.ALL_WEBSITE_VISITORS]: 'allVisitors',
  [VisitorRuleTypes.URL_CONTAINS]: 'urlContains',
  [VisitorRuleTypes.URL_EQUALS]: 'urlEquals',
  [VisitorRuleTypes.LANDING_PAGES]: 'landingPageIds',
  [VisitorRuleTypes.BLOG_PAGES]: 'blogPostIds',
  [VisitorRuleTypes.WEBSITE_PAGES]: 'websitePageIds'
};

const onSuccess = (dispatch, actionType, response, type = '', successCallback = null, trackingOpts = {}) => {
  dispatch({
    type: actionType,
    payload: Object.assign({}, response, {
      type
    }),
    meta: {
      notification: {
        type: 'success',
        message: type === AudienceTypes.ENGAGEMENT ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "audiences.wizard.banners.success"
        }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "audiences.wizard.banners.success_jsx",
          elements: {
            Link: UILink
          },
          options: {
            href: `/ads/${response.targetingAudience.portalId}/audiences/${response.targetingAudience.id}`
          }
        })
      }
    }
  });

  if (successCallback) {
    successCallback();
  }

  usageTracker.track('modifyAudience', Object.assign({
    action: 'Created audience',
    network: response.targetingAudience.adNetwork,
    type
  }, trackingOpts)); // ********** PUBLIC EVENT **********
  // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
  // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
  // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation

  usageTracker.track('createAudience', {
    network: response.targetingAudience.adNetwork,
    type
  });
  Metrics.counter('created-audience', {
    type,
    network: response.targetingAudience.network
  }).increment();
  return {
    type,
    audience: response
  };
};

const onFail = (dispatch, type, error, options, failCallback) => {
  dispatch({
    type: Actions.AUDIENCE_CREATE_FAILED,
    payload: {
      type,
      options
    },
    meta: error
  });

  if (failCallback) {
    failCallback();
  } else {
    throw error;
  }
};

const createGenericPixel = (pixel, failCallback = null) => {
  return dispatch => {
    return wrapReduxAction(AudiencesData.createPixel.bind(null, pixel), 'PIXEL_CREATE', {
      dispatch,
      success: response => {
        dispatch({
          type: Actions.PIXEL_CREATE_SUCCEEDED,
          payload: response
        });
        usageTracker.track('modifyPixel', {
          action: 'Create pixel',
          network: pixel.adNetwork
        });
        return response;
      },
      fail: failCallback,
      returnPromise: true
    });
  };
};

export const installGenericPixel = (pixel, failCallback = null) => {
  return dispatch => {
    const {
      adNetwork,
      accountId,
      id
    } = pixel;
    return wrapReduxAction(AudiencesData.installPixel.bind(null, {
      adNetwork,
      accountId,
      id
    }), 'PIXEL_INSTALL', {
      dispatch,
      success: response => {
        dispatch({
          type: Actions.PIXEL_INSTALL_SUCCEEDED,
          payload: {
            pixel,
            response
          }
        });
        usageTracker.track('modifyPixel', {
          action: 'Install pixel',
          network: pixel.adNetwork
        });
      },
      fail: failCallback,
      returnPromise: true
    });
  };
};
export const fetchAudiences = () => {
  return dispatch => wrapReduxAction(AudiencesData.getAudiences, 'AUDIENCES_FETCH', {
    dispatch
  });
};
export const fetchInactiveAudiences = () => {
  return dispatch => wrapReduxAction(AudiencesData.getInactiveAudiences, 'AUDIENCES_INACTIVE_FETCH', {
    dispatch
  });
};
export const sortAudiences = payload => {
  return dispatch => dispatch({
    type: Actions.AUDIENCES_SORT,
    payload
  });
};
export const setAccountsFilter = filter => {
  return dispatch => {
    dispatch({
      type: Actions.AUDIENCES_ACCOUNT_FILTER_CHANGE,
      payload: filter
    });
  };
};
export const fetchAudienceAdSequences = audienceId => {
  return AudiencesData.getAudienceAdSequences(audienceId);
};
export const deleteAudience = (audience, successCallback = null) => {
  const {
    adNetwork,
    audienceSource,
    type
  } = audience;
  return dispatch => {
    return wrapReduxAction(AudiencesData.deleteAudience.bind(null, audience.id), 'AUDIENCE_DELETE', {
      dispatch,
      success: () => {
        dispatch({
          type: Actions.AUDIENCE_DELETE_SUCCEEDED,
          payload: {
            audienceId: audience.id,
            seedTargetingAudienceId: audience.targetingAudience.seedTargetingAudienceId,
            type,
            audienceSource,
            adNetwork
          },
          meta: {
            notification: {
              type: 'success',
              message: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "audiences.wizard.banners.deleteSuccess"
              })
            }
          }
        });

        if (successCallback) {
          successCallback();
        }

        usageTracker.track('modifyAudience', {
          action: 'Deleted audience',
          network: audience.targetingAudience.adNetwork,
          type
        });
      },
      fail: () => {
        dispatch({
          type: Actions.AUDIENCE_DELETE_FAILED,
          payload: {
            audience
          },
          meta: {
            notification: {
              type: 'warning',
              message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "audiences.wizard.banners.deleteError"
              })
            }
          }
        });
      },
      returnPromise: true
    });
  };
};
export const getAudienceUsageInfo = () => {
  return dispatch => wrapReduxAction(AudiencesData.getAudienceActiveCounts, 'AUDIENCES_USAGE_INFO_FETCH', {
    dispatch
  });
};
export const getContactListsInboundDB = query => {
  return ContactsData.fetchContactListInboundDB(query).then(lists => lists.results.map(r => {
    const {
      hs_list_id,
      hs_list_name,
      hs_list_size
    } = r.properties;
    return {
      text: `${hs_list_name.value} (${hs_list_size ? hs_list_size.value : 0})`,
      value: `${hs_list_id.value}`,
      size: hs_list_size ? hs_list_size.value : 0,
      name: hs_list_name.value,
      key: hs_list_id.value
    };
  }));
};
export const getContactLists = (q = '') => {
  return ContactsData.fetchContactLists(q).then(lists => lists.lists.map(l => {
    const list = l.list;
    return {
      text: `${list.name} (${list.metaData.size})`,
      value: `${list.listId}`,
      size: list.metaData.size,
      name: list.name,
      key: list.listId
    };
  }));
};
export const getSyncedContactLists = () => AudiencesData.getSyncedContactLists();
export const fetchAudienceToS = options => {
  return AudiencesData.getAudienceToSForAccount(options).then(response => response);
};
export const fetchAudienceToSStatus = options => {
  return AudiencesData.getAudienceToSStatus(options);
};
export const getAccountsWithUnacceptedToS = (adNetwork, audienceSource) => {
  return dispatch => {
    wrapReduxAction(AudiencesData.getAccountsWithUnacceptedToS.bind(null, {
      adNetwork,
      audienceSource
    }), 'ACCOUNTS_WITH_UNACCEPTED_TOS_FETCH', {
      success: response => {
        dispatch({
          type: Actions.ACCOUNTS_WITH_UNACCEPTED_TOS_FETCH_SUCCEEDED,
          payload: response
        });

        if (response && response.length) {
          dispatch(addHeaderBanner(BannerTypes.FACEBOOK_AUDIENCE_TOS_CHANGED, [], response));
        }
      },
      dispatch
    });
  };
};
export const fetchAvailablePixels = () => {
  return AudiencesData.getPixels({
    includeOnlyInstalled: false
  });
};
export const fetchPixels = (returnPromise = false) => {
  return dispatch => {
    return wrapReduxAction(AudiencesData.getPixels.bind(null, {
      includeOnlyInstalled: false
    }), 'PIXELS_FETCH', {
      dispatch,
      returnPromise
    });
  };
}; // Used for bulk-accepting ToS for applicable ad accounts from the
// FacebookToSChangedBanner, which generally spawns when the ToS has changed.

export const acceptAudienceToSForAccounts = ({
  audienceSource,
  adNetworkAccountIdsMap
}) => {
  return dispatch => {
    wrapReduxAction(AudiencesData.acceptAudienceToSForAccounts.bind(null, {
      audienceSource,
      adNetworkAccountIdsMap
    }), 'ACCEPT_AUDIENCE_TOS_FOR_ACCOUNTS', {
      success: response => {
        dispatch({
          type: Actions.ACCEPT_AUDIENCE_TOS_FOR_ACCOUNTS_SUCCEEDED,
          payload: response
        });
        dispatch(removeHeaderBanner(BannerTypes.FACEBOOK_AUDIENCE_TOS_CHANGED, []));
      },
      dispatch
    });
  };
};
export const installAndCreatePixels = (selectedPixels, failCallback = null) => {
  // For any pixels don't exist yet, first create them then install.
  // For any existing pixels, we can immediately install them.
  return dispatch => {
    const pixelsToCreate = selectedPixels.filterNot(pixel => !!pixel.id);
    const existingPixels = selectedPixels.subtract(pixelsToCreate);
    const pixelPromises = [];

    if (!pixelsToCreate.isEmpty()) {
      pixelsToCreate.forEach(pixelToCreate => {
        pixelPromises.push(dispatch(createGenericPixel(pixelToCreate, failCallback)).then(createdPixel => {
          if (createdPixel) {
            return dispatch(installGenericPixel(createdPixel, failCallback));
          }

          return null;
        }));
      });
    }

    if (!existingPixels.isEmpty()) {
      existingPixels.forEach(pixel => {
        pixelPromises.push(dispatch(installGenericPixel(pixel, failCallback)));
      });
    }

    return Promise.all(pixelPromises);
  };
};
export const createRuleBasedWebsiteAudience = (options, failCallback = null) => {
  return dispatch => {
    return wrapReduxAction(AudiencesData.createRuleBasedAudience.bind(null, options), 'AUDIENCE_CREATE', {
      dispatch,
      success: response => {
        const {
          audienceRules
        } = response.targetingAudience;
        return onSuccess(dispatch, Actions.AUDIENCE_CREATE_SUCCEEDED, response, AudienceTypes.WEBSITE, null, {
          rules: Object.keys(audienceParamsMap).filter(ruleType => {
            // The "allVisitors" rule is a boolean, but all other rules
            // are specified as arrays, so check whether those are populated.
            if (ruleType === VisitorRuleTypes.ALL_WEBSITE_VISITORS) {
              return audienceRules.allVisitors;
            } else {
              return !!audienceRules[audienceParamsMap[ruleType]].length;
            }
          })
        });
      },
      fail: error => onFail(dispatch, AudienceTypes.WEBSITE, error, options, failCallback),
      returnPromise: true
    });
  };
};
export const createGenericContactListAudience = (options, failCallback = null) => {
  return (dispatch, getState) => {
    const remainingAudienceCount = getRemainingAudienceCount(getState());
    return wrapReduxAction(AudiencesData.createContactListAudience.bind(null, options), 'AUDIENCE_CREATE', {
      dispatch,
      success: response => {
        if (remainingAudienceCount <= 1) {
          usageTracker.track('modifyAudience', {
            action: 'Created last audience',
            type: AudienceTypes.CONTACT_LIST
          });
        }

        return onSuccess(dispatch, Actions.AUDIENCE_CREATE_SUCCEEDED, response, AudienceTypes.CONTACT_LIST);
      },
      fail: error => onFail(dispatch, AudienceTypes.CONTACT_LIST, error, options, failCallback),
      returnPromise: true
    });
  };
};
export const createGenericCompanyListAudience = (options, failCallback = null) => {
  return (dispatch, getState) => {
    const remainingAudienceCount = getRemainingAudienceCount(getState());
    return wrapReduxAction(AudiencesData.createCompanyListAudience.bind(null, options), 'AUDIENCE_CREATE', {
      dispatch,
      success: response => {
        if (remainingAudienceCount <= 1) {
          usageTracker.track('modifyAudience', {
            action: 'Created last audience',
            type: AudienceTypes.COMPANY_LIST
          });
        }

        return onSuccess(dispatch, Actions.AUDIENCE_CREATE_SUCCEEDED, response, AudienceTypes.COMPANY_LIST);
      },
      fail: error => onFail(dispatch, AudienceTypes.COMPANY_LIST, error, options, failCallback),
      returnPromise: true
    });
  };
};
export const createGenericLookalikeAudience = (options, successCallback = null, failCallback = null) => {
  return dispatch => {
    return wrapReduxAction(AudiencesData.createLookalikeAudience.bind(null, options), 'AUDIENCE_CREATE', {
      dispatch,
      success: response => onSuccess(dispatch, Actions.AUDIENCE_CREATE_SUCCEEDED, response, AudienceTypes.LOOKALIKE, successCallback),
      fail: error => onFail(dispatch, AudienceTypes.LOOKALIKE, error, options, failCallback),
      returnPromise: true
    });
  };
};
export const createGenericEngagementAudience = (options, successCallback = null, failCallback = null) => {
  return dispatch => {
    return wrapReduxAction(AudiencesData.createEngagementAudience.bind(null, options), 'AUDIENCE_CREATE', {
      dispatch,
      success: response => onSuccess(dispatch, Actions.AUDIENCE_CREATE_SUCCEEDED, response, AudienceTypes.ENGAGEMENT, successCallback),
      fail: error => onFail(dispatch, AudienceTypes.ENGAGEMENT, error, options, failCallback),
      returnPromise: true
    });
  };
}; // Used by audience creation modals to accept the ToS for any
// selected ad accounts with unaccepted ToS.

export const acceptAudienceToS = ({
  audienceSource,
  adAccounts
}) => {
  const adNetworkAccountIdsMap = adAccounts.reduce((adAccountIdMap, adAccount) => {
    if (!adAccountIdMap.has(adAccount.network)) {
      return adAccountIdMap.set(adAccount.network, ImmutableSet([adAccount.accountId]));
    } else {
      return adAccountIdMap.updateIn([adAccount.network], accountIds => accountIds.add(adAccount.accountId));
    }
  }, ImmutableMap());
  return AudiencesData.acceptAudienceToSForAccounts({
    audienceSource,
    adNetworkAccountIdsMap
  });
};
export const getSupportedAudiences = adAccount => {
  const {
    network,
    accountId
  } = adAccount;
  return dispatch => wrapReduxAction(AudiencesData.getSupportedAudiences.bind(null, network, accountId), 'SUPPORTED_AUDIENCES_FETCH', {
    dispatch,
    allParams: {
      adAccount
    }
  });
};
export const updateSupportedAudiences = (type, audience) => ({
  type: Actions.SUPPORTED_AUDIENCES_UPDATE,
  payload: Object.assign({}, audience, {
    type
  })
});
export const getSavedAudiences = adAccount => {
  const {
    network,
    accountId
  } = adAccount;

  if (network !== Networks.FACEBOOK) {
    // Doing it this way so this can eventually be reasonably network-agnostic; each network's
    // saved audiences work a bit differently so they'll need some individual attention.
    throw new Error('Saved audiences not supported for non-Facebook networks at this time.');
  }

  return dispatch => wrapReduxAction(AudiencesData.getFacebookSavedAudiences.bind(null, accountId), 'SAVED_AUDIENCES_FETCH', {
    dispatch,
    transform: savedAudiences => ({
      savedAudiences
    }),
    allParams: {
      adAccount
    }
  });
};
export const fetchMembershipCounts = contactListId => dispatch => wrapReduxAction(AudiencesData.getMembershipCounts.bind(null, contactListId), 'MEMBERSHIP_COUNTS_FETCH', {
  dispatch,
  allParams: {
    contactListId
  },
  transform: membershipCounts => ({
    membershipCounts
  })
});
export const fetchMembershipCountsInboundDB = contactListId => dispatch => wrapReduxAction(AudiencesData.getMembershipCountsInboundDB.bind(null, contactListId), 'MEMBERSHIP_COUNTS_FETCH', {
  dispatch,
  allParams: {
    contactListId
  },
  transform: membershipCounts => ({
    membershipCounts
  })
});
export const fetchMembershipCountsByAudienceId = audienceId => dispatch => wrapReduxAction(AudiencesData.getMembershipCountsByAudienceId.bind(null, audienceId), 'MEMBERSHIP_COUNTS_FETCH', {
  dispatch,
  allParams: {
    audienceId
  },
  transform: membershipCounts => ({
    membershipCounts
  })
});
export const fetchLinkedInFacetForLookalikeAudience = (adAccountId, audienceId) => dispatch => wrapReduxAction(AudiencesData.getLinkedInFacetForLookalikeAudience.bind(null, adAccountId, audienceId), 'LINKEDIN_AUDIENCE_FACET_FETCH', {
  dispatch,
  returnPromise: true,
  allParams: {
    adAccountId,
    audienceId
  },
  transform: adTargetingFacetType => ({
    adTargetingFacetType
  })
});